'use client';

import { useState, type ReactNode } from 'react';
import { css, cva } from '@mildang/styled-system/css';
import { VStack } from '@mildang/styled-system/jsx';
import { token } from '@mildang/styled-system/tokens';
import { Image } from '@mildang/design-system/Image';
import type { HeroImage } from './heroImages';

const logoSrc = new URL('./assets/logo_common.svg', import.meta.url).href;
// Figma noise 효과를 SVG로 export한 필터다. 화면 해상도로 매번 그려서 어느 배율에서도 선명하다.
const noiseSrc = new URL('../../../../assets/images/noise.svg', import.meta.url).href;
// 캐릭터는 패널 높이에 맞춘 정사각형이다. 폼 영역이 540px·화면 절반 이상을 쓰므로 휴대폰에서는 40vh를 넘지 않는다.
export const heroSizes = `(min-width: ${token('breakpoints.md')}) 100vh, (min-width: ${token('breakpoints.sm')}) 50vh, 40vh`;

// 앱마다 body 글꼴이 달라(CMS 18/30) 상속받는 라벨 줄 높이가 흔들리지 않게 기본 글꼴을 고정한다.
const rootStyle = css({ height: '100dvh', overflowY: 'auto', bg: 'neutral.surface.low', textStyle: 'body-md' });
const layoutStyle = css({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100%',
  md: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
});
const heroAreaStyle = css({
  display: 'flex',
  flex: '1 0 0',
  pt: '20',
  px: '20',
  sm: { pt: '32', px: '32' },
  md: { pb: '32', pr: '0' },
});
const heroStyle = cva({
  base: {
    position: 'relative',
    flex: '1',
    minHeight: '6rem',
    overflow: 'hidden',
    borderRadius: '16',
    sm: { borderRadius: '32' },
  },
  variants: {
    service: {
      study: { bgColor: 'green.light.700' },
      lms: { bgColor: 'blue.light.700' },
      cms: { bgColor: 'red.light.700' },
      crm: { bgColor: 'orange.light.650' },
      // 공교육 서비스의 브랜드 색. DS 토큰이 아닌 외부 서비스 색이라 시안 값을 그대로 쓴다.
      slearn: { bgColor: '[#6A1831]' },
      afterSchool: { bgColor: '[#5C4DFD]' },
      laschool: { bgColor: '[#F9A70C]' },
    },
  },
});
const heroImageStyle = css({
  position: 'absolute',
  top: '0',
  bottom: '0',
  left: '50%',
  aspectRatio: '1',
  transform: 'translateX(-50%)',
  backgroundSize: 'cover',
});
const heroImgStyle = css({ position: 'absolute', inset: '0', w: '100%', h: '100%' });
const heroLogoStyle = css({
  position: 'absolute',
  top: '16',
  left: '16',
  width: 'auto',
  height: 'token(spacing.24)',
  sm: { top: '24', left: '24', height: 'token(spacing.32)' },
  md: {
    top: 'calc(token(spacing.28) + token(spacing.2))',
    left: 'calc(token(spacing.28) + token(spacing.2))',
    height: 'token(spacing.36)',
  },
});
const formAreaStyle = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 'max(33.75rem, 50dvh)',
  pt: '24',
  px: '24',
  pb: '32',
  sm: { pt: '32', px: '32' },
  md: { minHeight: 'auto', pl: '0' },
});
const logoSlotStyle = css({
  display: 'grid',
  placeItems: 'center',
  height: 'calc(token(spacing.28) + token(spacing.2))',
});
const titleStyle = css({ textStyle: 'headline-md', color: 'neutral.text.base' });
const descriptionStyle = css({ textStyle: 'body-md', color: 'neutral.text.lowest' });

export type LoginService = 'study' | 'lms' | 'cms' | 'crm' | 'slearn' | 'afterSchool' | 'laschool';

interface LoginLayoutProps {
  service: LoginService;
  heroImage: HeroImage;
  /** 캐릭터 패널 왼쪽 위 흰 로고(높이 36 프레임). 없으면 밀당 로고 */
  heroLogo?: { src: string; width: number; height: number };
  logo: ReactNode;
  title: ReactNode;
  description: ReactNode;
  children: ReactNode;
}

// 미리 줄여 둔 WebP를 화면 크기에 맞게 고른다. 받기 전에는 흐린 미리보기를 깔고, 다 받으면 걷어낸다(캐릭터 배경이 투명이라).
function HeroImg({ image }: { image: HeroImage }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <div className={heroImageStyle} style={loaded ? undefined : { backgroundImage: `url(${image.placeholder})` }}>
      {/* eslint-disable-next-line @next/next/no-img-element -- Next 이미지 변환을 거치지 않으려고 직접 그린다 */}
      <img
        src={image.src}
        srcSet={image.srcSet}
        sizes={heroSizes}
        alt=""
        fetchPriority="high"
        draggable={false}
        className={heroImgStyle}
        onLoad={() => setLoaded(true)}
      />
    </div>
  );
}

export default function LoginLayout({
  service,
  heroImage,
  heroLogo,
  logo,
  title,
  description,
  children,
}: LoginLayoutProps) {
  return (
    <main className={rootStyle}>
      <div className={layoutStyle}>
        <div className={heroAreaStyle} aria-hidden>
          <div className={heroStyle({ service })} style={{ backgroundImage: `url(${noiseSrc})` }}>
            <HeroImg image={heroImage} />
            <Image
              src={heroLogo?.src ?? logoSrc}
              alt=""
              width={heroLogo?.width ?? 86}
              height={heroLogo?.height ?? 36}
              unoptimized
              draggable={false}
              className={heroLogoStyle}
            />
          </div>
        </div>
        <div className={formAreaStyle}>
          <VStack alignItems="stretch" gap={{ base: '24', sm: '32' }} width="100%" maxWidth="23.75rem">
            <VStack gap="16" textAlign="center">
              <h1 className={logoSlotStyle}>{logo}</h1>
              <VStack gap="6">
                <p className={titleStyle}>{title}</p>
                <p className={descriptionStyle}>{description}</p>
              </VStack>
            </VStack>
            {children}
          </VStack>
        </div>
      </div>
    </main>
  );
}
